<script setup lang="ts">
import { inject, ref } from 'vue';
import {
  PrivacyPolicyPath,
  ResetPasswordPath,
  SignInPath, SmsTextPolicyPath,
  TermsOfUsePolicyPath,
  UserLocale
} from "@/constants";
import validator from "validator";
import { Form } from "@primevue/forms";
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from "zod";
import { useApplicationStore } from "@/stores/Application";
import { storeToRefs } from "pinia";
import { useUrlSearchParams } from '@vueuse/core';
import { RuntimeException } from "@/types/errors";
import type { LabelService } from "@/composables/label-service";
import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;

const appStore = useApplicationStore();
const { isLoading } = storeToRefs( appStore );
const errorMessage = ref< string >();
const infoMessage = ref<string>();
const urlSearchParams = useUrlSearchParams();
const nonEquivalentPasswordsErrorMessage = `The passwords do not match`;

type SignupInfo = {
  firstName: string,
  lastName: string,
  phoneNumber: string,
  password: string,
  passwordConfirmation: string,
  email: string,
  acceptTerms: boolean
}

const initialValues = ref< SignupInfo >( {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    password: '',
    passwordConfirmation: '',
    email: '',
    acceptTerms: false
} );

const resolver = zodResolver(
  z.object( {
    firstName: z.string()
      .max( 50, { message: 'Maximum 50 characters' } )
      .refine( val => !validator.isEmpty( val.trim() ), 'First name is required' ),
    lastName: z.string()
      .max( 50, { message: 'Maximum 50 characters' } )
      .refine( val => !validator.isEmpty( val.trim() ), { message: 'Last name is required' } ),
    email: z.string()
      .email()
      .refine( val => !validator.isEmpty( val.trim() ), { message: 'Email is required' } ),
    phoneNumber: z
      .string()
      .trim()
      .min(1, { message: 'Phone number is required' })
      .refine( val => validator.isMobilePhone(val, UserLocale), {
        message: 'Please provide a valid phone number',
      }),
    password: z.string()
      .refine( val => !validator.isEmpty( val.trim() ), { message: 'Password is required' } ),
    passwordConfirmation: z.string()
      .refine( val => !validator.isEmpty( val.trim() ), { message: 'Password Confirmation is required' } ),
    acceptTerms:
      z.boolean()
      .refine( val => val, { message: 'You must accept the terms and conditions.' } ),
    myEmail:
      z.string().nullable()
  } ).superRefine( ( { passwordConfirmation, password }, ctx ) => {
      if ( passwordConfirmation !== password ) {
        ctx.addIssue( {
          code: "custom",
          message: nonEquivalentPasswordsErrorMessage,
          path: [ "passwordConfirmation" ]
        } );
      }
    } )
);

const removeBeforeColon = (str: string) => {
  // Split the string at the first colon and return the second part (everything after the colon)
  return str.split(':').slice(1).join(':').trim();
}

const signUp = async ( e: any ) => {
  if ( !e.valid ) {
    return null;
  }

  if(e.values.myEmail) {
    return;
  }

  const utmAdParamValue: string = Array.isArray(urlSearchParams.utm_ad) ? urlSearchParams.utm_ad[0] : urlSearchParams.utm_ad;

  const credentials = {
    firstName: e.values.firstName,
    lastName: e.values.lastName,
    phoneNumber: e.values.phoneNumber,
    password: e.values.password,
    passwordConfirmation: e.values.passwordConfirmation,
    email: e.values.email,
    utm_ad: utmAdParamValue,
  };

  appStore
      .signUp( credentials )
      .catch( ( err: any ) => {
        // TODO: have an error service that provides this value in case the phone number ever needs to change - one place to update it
        const message: string = err?.message ?? 'An unknown error has occurred, please contact customer support at (855) 822-0269';
        if ( typeof err === 'object' && err instanceof RuntimeException ) {
          if ( message.toLocaleLowerCase().indexOf( 'email already exists' ) > -1 ) {
            errorMessage.value = '';
            infoMessage.value = credentials.email;
          } else {
            // For any other errors, set the error message to the actual message
            errorMessage.value = removeBeforeColon( message );
            infoMessage.value = '';
          }
        } else {
          errorMessage.value = 'There was a problem with your submission. Please review the fields above and try again.';
        }
      } );
};
</script>

<template>
    <Form v-slot="$form" :resolver :validate-on-submit="true" :validate-on-blur="true" :initialValues @submit="signUp" class="flex flex-col gap-5 w-full">
      <template v-if="!infoMessage">
        <div>
            <FloatLabel variant="on">
                <InputText
                    name="email"
                    id="email"
                    type="text"
                    fluid
                />
                <label for="email">Email address*</label>
            </FloatLabel>
            <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">{{ $form.email?.error.message }}</Message>
        </div>

        <div>
            <FloatLabel variant="on">
                <InputText
                    name="firstName"
                    id="firstName"
                    type="text"
                    fluid
                />
                <label for="firstName">First name*</label>
            </FloatLabel>
            <Message v-if="$form.firstName?.invalid" severity="error" size="small" variant="simple">{{ $form.firstName?.error.message }}</Message>
        </div>
        <div>
            <FloatLabel variant="on">
                <InputText
                    name="lastName"
                    id="lastName"
                    type="text"
                    fluid
                />
                <label for="lastName">Last name*</label>
            </FloatLabel>
            <Message v-if="$form.lastName?.invalid" severity="error" size="small" variant="simple">{{ $form.lastName?.error.message }}</Message>
        </div>

        <div>
            <FloatLabel variant="on">
                <InputText
                    name="phoneNumber"
                    id="phoneNumber"
                    type="text"
                    fluid
                />
                <label for="phoneNumber">Mobile Number*</label>
            </FloatLabel>
            <Message v-if="$form.phoneNumber?.invalid" severity="error" size="small" variant="simple">{{ $form.phoneNumber?.error.message }}</Message>
        </div>

        <div>
            <FloatLabel variant="on">
                <InputText
                    name="password"
                    id="password"
                    type="password"
                    fluid
                />
                <label for="password">Password*</label>
            </FloatLabel>
            <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">{{ $form.password?.error.message }}</Message>
        </div>

        <div>
            <FloatLabel variant="on">
                <InputText
                    name="passwordConfirmation"
                    id="passwordConfirmation"
                    type="password"
                    fluid
                />
                <label for="passwordConfirmation">Password Confirmation*</label>
            </FloatLabel>
            <Message v-if="$form.passwordConfirmation?.invalid" severity="error" size="small" variant="simple">{{ $form.passwordConfirmation?.error.message }}</Message>
        </div>


      <FormField v-slot="$field" name="myEmail" initialValue="" style="position: absolute; left: -9999px">
          <InputText
              name="myEmail"
              variant="filled"
              inputId="myEmail"
              type="text"
              autocomplete="off"
              tabindex="-1"
              />
      </FormField>

        <div>
            <div class="flex items-start gap-2">
                <div class="flex-shrink-0 mt-0">
                    <Checkbox binary name="acceptTerms" id="acceptTerms" size="small" />
                </div>
                <label for="acceptTerms" class="mt-1 text-sm">
                  I AGREE TO RECEIVE MARKETING, by entering your info and checking the box, you provide your electronic signature, express written consent and binding agreement to our <router-link :to="TermsOfUsePolicyPath" target="_blank" tabindex="-1">{{ labelService.label( 'lblTermsAndConditions' ) }}</router-link>, <router-link :to="PrivacyPolicyPath" target="_blank" tabindex="-1">{{ labelService.label( 'lblPrivacyPolicy' ) }}</router-link>, <router-link :to="SmsTextPolicyPath" target="_blank" tabindex="-1">{{ labelService.label( 'lblSmsPolicy' ) }}</router-link>, and for True Market Insiders, its affiliates, agents, and service providers, to email, call and text you at the email address and telephone number you provided above (or later), including possibly using an automatic telephone dialing system, prerecorded messages and artificial/A.I. voices, with editorial, marketing offers, and other info. This agreement is legally binding just like a signed hard copy and will be retained in a format you can print or request from us later. You agree to do business with us electronically but you may opt-out later. Frequency of text messages may vary; reply STOP to opt-out. Normal rates apply. This website may record your interactions with it and future calls may be recorded/monitored.
                </label>
            </div>
            <Message v-if="$form.acceptTerms?.invalid" severity="error" size="small" variant="simple">{{ $form.acceptTerms?.error.message }}</Message>
        </div>

        <button type="submit" class="btn btn-primary" :disabled="!( $form.valid )">
            <span v-if="isLoading" class="w-6 h-6 rounded-full animate-spin border-2 border-solid border-base-100 border-t-transparent"></span>
            <span v-else>Sign Up</span>
        </button>
        </template>
        <Message v-if="errorMessage" severity="error" size="small" icon="pi pi-times-circle">
            {{ errorMessage }}
        </Message>
        <template v-if="infoMessage">
          <Message size="large" icon="pi pi-times-circle">
            <p>Whoops! It looks like you already have an account with us.</p>
          </Message>
          <p>You can either try signing in to your existing account (<b>{{ infoMessage }}</b>) or reset your password if you’ve forgotten it.</p>
          <router-link class="btn btn-primary" :to="SignInPath">
            Sign In
          </router-link>
          <router-link class="btn btn-light" :to="ResetPasswordPath">
            Reset Your Password
          </router-link>
        </template>
    </Form>
</template>
